import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Spin, Result, Button } from "antd";
import {
  SafetyCertificateOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { db, storage } from "../../services/firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import "./styles.css";

function PublicCertificate() {
  const { id } = useParams();
  const [certificado, setCertificado] = useState(null);
  const [clienteNome, setClienteNome] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [filesCertificados, setFilesCertificados] = useState([]);
  const [filesDatabook, setFilesDatabook] = useState([]);

  const fetchFromCollection = async (name) => {
    const snapshot = await getDocs(collection(db, name));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        const snap = await getDoc(doc(db, "certificados", id));
        if (!snap.exists()) { setNotFound(true); return; }

        const data = { id: snap.id, ...snap.data() };
        setCertificado(data);

        // resolve nome do cliente
        if (data.cliente_id) {
          const clienteSnap = await getDoc(doc(db, "clientes", data.cliente_id));
          if (clienteSnap.exists()) {
            setClienteNome(clienteSnap.data().nomeEmpresa || "");
          }
        }

        // arquivos PDF
        try {
          const [resCert, resData] = await Promise.all([
            listAll(ref(storage, `${id}/certificados`)),
            listAll(ref(storage, `${id}/databook`)),
          ]);
          const [urls1, urls2] = await Promise.all([
            Promise.all(resCert.items.map((f) => getDownloadURL(f))),
            Promise.all(resData.items.map((f) => getDownloadURL(f))),
          ]);
          setFilesCertificados(urls1);
          setFilesDatabook(urls2);
        } catch (_) {}
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="pc-loading">
        <Spin size="large" tip="Carregando certificado..." />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="pc-loading">
        <Result
          status="404"
          title="Certificado não encontrado"
          subTitle="O link do QR Code pode estar desatualizado ou o certificado foi removido."
        />
      </div>
    );
  }

  const field = (label, value) =>
    value ? (
      <div className="pc-field">
        <span className="pc-label">{label}</span>
        <span className="pc-value">{value}</span>
      </div>
    ) : null;

  return (
    <div className="pc-page">
      {/* Header */}
      <header className="pc-header">
        <img src="/assets/logo-branca1.png" alt="Seepil Valve Care" className="pc-logo" />
      </header>

      {/* Card */}
      <main className="pc-main">
        <div className="pc-card">
          {/* Título */}
          <div className="pc-card-header">
            <div className="pc-card-icon">
              <SafetyCertificateOutlined />
            </div>
            <div>
              <h1 className="pc-card-title">Certificado de Válvula</h1>
              <p className="pc-card-subtitle">Seepil Valve Care — documento verificado</p>
            </div>
          </div>

          {/* Seção: Identificação */}
          <div className="pc-section">
            <h2 className="pc-section-title">Identificação</h2>
            <div className="pc-fields-grid">
              {field("Cliente", clienteNome)}
              {field("Unidade", certificado.unidadeCliente)}
              {field("Tag", certificado.tag)}
              {field("Nº Certificado", certificado.numeroCertificado)}
              {field("Controle Interno SPL", certificado.spl)}
              {field("Tipo de Certificado", certificado.tipoCertificado?.toUpperCase())}
            </div>
          </div>

          <div className="pc-divider" />

          {/* Seção: Dados da Válvula */}
          <div className="pc-section">
            <h2 className="pc-section-title">Dados da Válvula</h2>
            <div className="pc-fields-grid">
              {field("Tipo da Válvula", certificado.tipoValvula)}
              {field("Fabricante", certificado.fabricante)}
              {field("Modelo", certificado.modelo)}
              {field("Número de Série", certificado.numeroSerie)}
            </div>
          </div>

          <div className="pc-divider" />

          {/* Seção: Calibração */}
          <div className="pc-section">
            <h2 className="pc-section-title">Calibração</h2>
            <div className="pc-fields-grid">
              {field("Data de Calibração", certificado.dataCalibracao)}
              {field("Próxima Calibração", certificado.proximaCalibracao)}
            </div>
          </div>

          {/* Documentos */}
          {(filesCertificados.length > 0 || filesDatabook.length > 0) && (
            <>
              <div className="pc-divider" />
              <div className="pc-section">
                <h2 className="pc-section-title">Documentos</h2>
                <div className="pc-docs">
                  {filesCertificados.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="pc-doc-btn">
                      <FilePdfOutlined />
                      Certificado{filesCertificados.length > 1 ? ` (${i + 1})` : ""}
                    </a>
                  ))}
                  {filesDatabook.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="pc-doc-btn pc-doc-btn--secondary">
                      <FilePdfOutlined />
                      Databook{filesDatabook.length > 1 ? ` (${i + 1})` : ""}
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="pc-footer">
        Seepil Valve Care &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}

export default PublicCertificate;
